import type {
  ChatInterpretation,
  DiscoveryRequest,
  DiscoveryCapabilities,
  DiscoveryRun,
  EffectOption,
  EvidenceRecord,
  GraphDto,
  InteractionHypothesis,
  Meta,
  Paged,
  PairAudit,
  PairResult,
  PairConnection,
  Stack,
  SubstanceDetail,
  SubstanceSummary,
  SubstanceChemistry,
  StackDiscoveryRequestV2,
} from './types'

export class ApiError extends Error {
  status: number
  payload: Record<string, unknown>

  constructor(status: number, payload: Record<string, unknown>) {
    super(String(payload.error || `API request failed with ${status}`))
    this.status = status
    this.payload = payload
  }
}

const configuredApiBase = String(import.meta.env.VITE_BIOGRAPH_API_BASE_URL || '').trim().replace(/\/$/, '')
const apiBase = configuredApiBase || '/api'

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  })
  const payload = await response.json().catch(() => ({ error: 'The API returned invalid JSON.' }))
  if (!response.ok) throw new ApiError(response.status, payload)
  return payload as T
}

function queryString(values: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const result = params.toString()
  return result ? `?${result}` : ''
}

export const repository = {
  getMeta: () => api<Meta>('/meta'),
  getDiscoveryCapabilities: () => api<DiscoveryCapabilities>('/discovery/capabilities'),

  listSubstances: (filters: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<SubstanceSummary>>(`/substances${queryString(filters)}`),

  getSubstance: (id: string) => api<SubstanceDetail>(`/substances/${encodeURIComponent(id)}`),
  getSubstanceChemistry: (id: string) => api<SubstanceChemistry>(`/substances/${encodeURIComponent(id)}/chemistry`),
  getSubstanceGraph: (id: string) => api<GraphDto>(`/substances/${encodeURIComponent(id)}/graph`),

  listEffects: (filters: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<EffectOption>>(`/effects${queryString(filters)}`),

  listInteractions: (filters: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<InteractionHypothesis>>(`/interactions${queryString(filters)}`),

  getPair: (a: string, b: string) => api<PairResult>(`/interactions/pair${queryString({ a, b })}`),
  getPairConnection: (a: string, b: string) => api<PairConnection>(`/interactions/pair/summary${queryString({ a, b })}`),

  listStacks: (filters: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<Stack>>(`/stacks${queryString(filters)}`),

  getStack: (id: string) => api<Stack>(`/stacks/${encodeURIComponent(id)}`),

  discover: async (request: DiscoveryRequest) => {
    const run = await api<DiscoveryRun>('/discovery', { method: 'POST', body: JSON.stringify(request) })
    sessionStorage.setItem(`biograph-run:${run.id}`, JSON.stringify(run))
    return run
  },

  interpretGoal: (request: StackDiscoveryRequestV2) =>
    api<ChatInterpretation>('/chat/interpret', { method: 'POST', body: JSON.stringify(request) }),

  discoverV2: async (request: StackDiscoveryRequestV2) => {
    const run = await api<DiscoveryRun>('/discovery/v2', { method: 'POST', body: JSON.stringify(request) })
    sessionStorage.setItem(`biograph-run:${run.id}`, JSON.stringify(run))
    return run
  },

  getRun: async (id: string) => {
    try {
      return await api<DiscoveryRun>(`/runs/${encodeURIComponent(id)}`)
    } catch (error) {
      const cached = sessionStorage.getItem(`biograph-run:${id}`)
      if (cached) return JSON.parse(cached) as DiscoveryRun
      throw error
    }
  },

  auditCustomStack: (substanceIds: string[]) =>
    api<{ substanceIds: string[]; audit: PairAudit; graph: GraphDto }>('/custom-stack/audit', {
      method: 'POST',
      body: JSON.stringify({ substanceIds }),
    }),

  listEvidence: (filters: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<EvidenceRecord> & { categories: Record<string, number> }>(`/evidence${queryString(filters)}`),
}
